import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

export interface ActionButton {
  action: string;
  icon: string;
  title: string;
  cssClass: string;
  permission: string;
  visible: boolean;
  enabled: boolean;
}

export interface RowData {
  id: string;
  isActive?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-generic-action-cell-renderer',
  template: `
    <div class="action-buttons d-flex gap-2 align-items-center justify-content-center">
      <!-- Edit Button -->
      <button 
        class="btn btn-outline-primary btn-sm" 
        (click)="onAction('edit')" 
        title="Edit {{ entityDisplayName }}" 
        type="button"
        *ngIf="canEdit"
        [disabled]="!canEdit">
        <i class="fas fa-edit"></i>
      </button>
      
      <!-- Show disabled edit button for read-only users -->
      <button 
        class="btn btn-outline-secondary btn-sm" 
        title="Edit not allowed - Read only access" 
        type="button"
        *ngIf="!canEdit && canRead"
        disabled>
        <i class="fas fa-edit text-muted"></i>
      </button>
      
      <!-- Create Tag Button (only for entities that support it) -->
      <button 
        class="btn btn-outline-primary btn-sm" 
        (click)="onAction('createTag')" 
        title="Create Tag" 
        type="button"
        *ngIf="showCreateTag && canEdit">
        <i class="fas fa-tag"></i>
      </button>
      
      <!-- Manage Button (for user management, etc.) -->
      <button 
        class="btn btn-outline-info btn-sm" 
        (click)="onAction('manage')" 
        title="Manage {{ entityDisplayName }}" 
        type="button"
        *ngIf="showManage && canEdit">
        <i class="fas fa-cogs"></i>
      </button>
      
      <!-- Delete Button - Only show when status is inactive -->
      <button
        class="btn btn-outline-primary btn-sm"
        (click)="onAction('delete')"
        title="Delete {{ entityDisplayName }}"
        type="button"
        *ngIf="canDelete && canShowDelete"
        [disabled]="!canDelete">
        <i class="fas fa-trash"></i>
      </button>

      <!-- Show disabled delete button for read-only users or when status is active -->
      <span [title]="getDeleteDisabledTooltip()"><button
        class="btn btn-outline-secondary btn-sm"
        type="button"
        *ngIf="(!canDelete && canRead) || (canDelete && !canShowDelete)"
        disabled>
        <i class="fas fa-trash text-muted"></i>
      </button></span>
    </div>`,
})
export class GenericActionCellRendererComponent implements ICellRendererAngularComp {
  private params!: ICellRendererParams;

  // Permission flags
  canEdit: boolean = false;
  canDelete: boolean = false;
  canRead: boolean = false;

  // Action visibility flags
  showCreateTag: boolean = false;
  showManage: boolean = false;

  // Delete button visibility based on status
  canShowDelete: boolean = false;

  // Entity display name
  entityDisplayName: string = '';
  
  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.updatePermissions();
  }
  
  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.updatePermissions();
    return true;
  }
  
  private updatePermissions(): void {
    if (this.params.context && this.params.context.componentParent) {
      const parent = this.params.context.componentParent;

      // Get permissions from parent component
      this.canEdit = parent.canEditEntity();
      this.canDelete = parent.canDeleteEntity();
      this.canRead = parent.canReadEntity();

      // Get action visibility from parent configuration
      this.showCreateTag = parent.showCreateTagAction ? parent.showCreateTagAction() : false;
      this.showManage = parent.showManageAction ? parent.showManageAction() : false;

      // Get entity display name
      this.entityDisplayName = parent.getEntityDisplayName ? parent.getEntityDisplayName() : 'item';

      // Check if delete should be shown based on status (only show when inactive)
      const rowData = this.params.data as RowData;
      if (rowData && rowData.hasOwnProperty('isActive')) {
        // Delete button is only visible when status is inactive (isActive === false)
        this.canShowDelete = rowData.isActive === false;
      } else {
        // If no isActive property, show delete button (backward compatibility)
        this.canShowDelete = true;
      }
    }
  }

  getDeleteDisabledTooltip(): string {
    if (!this.canDelete && this.canRead) {
      return 'Delete not allowed - Read only access';
    }
    if (this.canDelete && !this.canShowDelete) {
      return `Please Inactive ${this.entityDisplayName} first then system allow to delete ${this.entityDisplayName}.`;
    }
    return 'Delete not allowed';
  }
  
  onAction(action: string): void {
    if (!this.params.context || !this.params.context.componentParent) {
      return;
    }
    
    const parent = this.params.context.componentParent;
    const entityId = this.params.data.id;
    
    // Map actions to parent component methods
    switch (action) {
      case 'edit':
        if (this.canEdit && parent.editEntity) {
          parent.editEntity(entityId);
        }
        break;
      case 'delete':
        if (this.canDelete && parent.deleteEntity) {
          parent.deleteEntity(entityId);
        }
        break;
      case 'createTag':
        if (this.canEdit && parent.createTag) {
          parent.createTag(entityId);
        }
        break;
      case 'manage':
        if (this.canEdit && parent.manageEntity) {
          parent.manageEntity(entityId);
        }
        break;
    }
  }
}